/**
 * Bundle format detection utilities.
 *
 * @example
 * ```typescript
 * import { BundleFormat, AITReader } from "@apps-in-toss/ait-format";
 *
 * const buffer = await fs.readFile("bundle.ait");
 * const format = BundleFormat.detect(buffer);
 *
 * if (format === BundleFormat.Format.AIT) {
 *   const reader = AITReader.fromBuffer(buffer);
 *   // Process AIT bundle
 * } else if (format === BundleFormat.Format.ZIP) {
 *   // Process legacy ZIP bundle
 * }
 * ```
 */

import { MAGIC } from "./constants";

/** ZIP local file header magic (PK\x03\x04) */
const ZIP_MAGIC = new Uint8Array([0x50, 0x4b, 0x03, 0x04]);

/**
 * Bundle format types.
 */
export enum Format {
  /** AIT bundle format (AITBUNDL magic) */
  AIT = "AIT",
  /** Legacy ZIP format (PK magic) */
  ZIP = "ZIP",
  /** Unknown format */
  UNKNOWN = "UNKNOWN",
}

/**
 * Bundle format detection utilities.
 */
export const BundleFormat = {
  /**
   * Format enum for convenience.
   */
  Format,

  /**
   * Detect the bundle format from a buffer.
   *
   * @param buffer - The buffer to detect format from (only first 8 bytes are needed)
   * @returns The detected format
   */
  detect(buffer: Uint8Array): Format {
    // Check AIT magic (8 bytes)
    if (buffer.length >= MAGIC.length) {
      let isAIT = true;
      for (let i = 0; i < MAGIC.length; i++) {
        if (buffer[i] !== MAGIC[i]) {
          isAIT = false;
          break;
        }
      }
      if (isAIT) {
        return Format.AIT;
      }
    }

    // Check ZIP magic (4 bytes)
    if (buffer.length >= ZIP_MAGIC.length) {
      let isZIP = true;
      for (let i = 0; i < ZIP_MAGIC.length; i++) {
        if (buffer[i] !== ZIP_MAGIC[i]) {
          isZIP = false;
          break;
        }
      }
      if (isZIP) {
        return Format.ZIP;
      }
    }

    return Format.UNKNOWN;
  },

  /**
   * Check if the buffer represents an AIT format.
   *
   * @param buffer - The buffer to check (only first 8 bytes are needed)
   * @returns true if AIT format
   */
  isAIT(buffer: Uint8Array): boolean {
    if (buffer.length < MAGIC.length) {
      return false;
    }
    for (let i = 0; i < MAGIC.length; i++) {
      if (buffer[i] !== MAGIC[i]) {
        return false;
      }
    }
    return true;
  },

  /**
   * Check if the buffer represents a ZIP format.
   *
   * @param buffer - The buffer to check (only first 4 bytes are needed)
   * @returns true if ZIP format
   */
  isZIP(buffer: Uint8Array): boolean {
    if (buffer.length < ZIP_MAGIC.length) {
      return false;
    }
    for (let i = 0; i < ZIP_MAGIC.length; i++) {
      if (buffer[i] !== ZIP_MAGIC[i]) {
        return false;
      }
    }
    return true;
  },
} as const;
